import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { useGetTasksDataTable } from 'generated/resources/datatable.graphql'
import { TasksValueObjectsTaskIdentifierEnum } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { DataTableActions } from 'modules/tasks/components/TasksList/components/DataTableActions'
import { EmptyState } from 'modules/tasks/components/TasksList/components/EmptyState'
import { filters as dataTableFilters } from 'modules/tasks/components/TasksList/components/Filters'
import { projections } from 'modules/tasks/components/TasksList/components/projections'
import { useTaskPath } from 'modules/tasks/hooks/useTaskPath'

interface Props {
  identifier?: TasksValueObjectsTaskIdentifierEnum
  involveeId?: number
  hideActions?: boolean
  isExternalUser: boolean
  employeeId?: number
  canManageTasks?: boolean
}

export const TasksDataTable = ({
  involveeId,
  identifier,
  hideActions,
  isExternalUser,
  employeeId,
  canManageTasks,
}: Props) => {
  const { navigation } = useNavigation()
  const { search, onSearch, debouncedSearch } = useDebouncedSearch()

  const involveeData = involveeId
    ? {
        involveeId,
      }
    : {}

  const variables = {
    search: debouncedSearch,
    ...involveeData,
    identifier,
  }

  const taskPath = useTaskPath()

  if (taskPath.error || !taskPath.path) throw new Error()

  const source = useGetTasksDataTable(projections(), {
    variables,
  })
  const navigate = useNavigate()

  return (
    <DataTable.Provider
      filters={dataTableFilters(source)}
      source={source}
      pagination={{
        kind: 'offset',
        itemsPerPage: 15,
        infiniteScroll: false,
      }}
      sort={{
        path: 'createdAt',
        direction: 'desc',
      }}
      rowTo={({ id }) =>
        navigation.tasks.taskIdAndIdentifier.fullPath({
          taskId: id,
          identifier: '',
        })
      }
    >
      <Box gap="s16">
        <DataTable.HeadBar
          disableExport
          search={{
            value: search,
            onChange: onSearch,
            placeholder: i18n.t('common.search_by'),
          }}
          actions={
            <DataTableActions
              status={status}
              identifier={identifier}
              employeeId={employeeId}
              hideActions={hideActions}
              isExternalUser={isExternalUser}
              canManageTasks={canManageTasks}
            />
          }
          customPopoverOptions={
            <>
              {!isExternalUser && canManageTasks && (
                <>
                  <PopoverMenu.TouchableOption
                    title={`${i18n.t('processes.tasks.import.title')}`}
                    description={`${i18n.t('processes.tasks.import.description')}`}
                    onClick={() =>
                      navigate(
                        navigation.employees.id.tasks.import.fullPath({ id: String(employeeId) })
                      )
                    }
                  />
                  <PopoverMenu.TouchableOption
                    title={`${i18n.t('processes.tasks.manage.title')}`}
                    description={`${i18n.t('processes.tasks.manage.description')}`}
                    onClick={() => navigate('/workflows')}
                  />
                </>
              )}
            </>
          }
        />
        <DataTable.Table autoWidth selectable={() => true} emptyState={<EmptyState />} />
      </Box>
    </DataTable.Provider>
  )
}
